import { useState, useEffect } from "react";
import { api, parseISODate, formatLocalISO } from "../lib/utils";
import { toast } from "sonner";
import { Calendar, Plus } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar as CalendarComponent } from "./ui/calendar";
import { format, addWeeks } from "date-fns";

export const NewOrderDialog = ({ open, onOpenChange, statuses, sources, onSuccess, invoiceId, customerName }) => {
  const defaultDueDate = addWeeks(new Date(), 3);

  const [formData, setFormData] = useState({
    customer_name: customerName || "",
    source: sources[0]?.name || "",
    due_date: format(defaultDueDate, "yyyy-MM-dd"),
    status: statuses[0]?.name || "",
    notes: "",
    invoice_id: invoiceId || null,
  });
  const [saving, setSaving] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  // Update form when props change (e.g., opening from an invoice)
  useEffect(() => {
    if (open) {
      setFormData((prev) => ({
        ...prev,
        customer_name: customerName || prev.customer_name,
        source: sources[0]?.name || prev.source,
        status: statuses[0]?.name || prev.status,
        invoice_id: invoiceId || null,
      }));
    }
  }, [open, customerName, invoiceId, sources, statuses]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.customer_name.trim()) {
      toast.error("Please enter a customer name");
      return;
    }
    if (!formData.source) {
      toast.error("Please select a source");
      return;
    }
    if (!formData.status) {
      toast.error("Please select a status");
      return;
    }

    setSaving(true);
    try {
      const endpoint = invoiceId
        ? `/invoices/${invoiceId}/create-order`
        : "/orders";
      await api.post(endpoint, formData);
      toast.success("Order created successfully!");

      // Reset form
      setFormData({
        customer_name: "",
        source: sources[0]?.name || "",
        due_date: format(addWeeks(new Date(), 3), "yyyy-MM-dd"),
        status: statuses[0]?.name || "",
        notes: "",
        invoice_id: null,
      });

      onSuccess();
    } catch (error) {
      toast.error(error.message || "Failed to create order");
    } finally {
      setSaving(false);
    }
  };

  const handleDateSelect = (date) => {
    if (date) {
      setFormData({ ...formData, due_date: formatLocalISO(date) });
      setDatePickerOpen(false);
    }
  };

  const formatDisplayDate = (dateStr) => {
    try {
      return format(parseISODate(dateStr), "MMMM d, yyyy");
    } catch {
      return dateStr;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-primary" />
            New Order
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label>Customer Name *</Label>
            <Input
              value={formData.customer_name}
              onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
              placeholder="Enter customer name..."
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label>Source *</Label>
            <Select value={formData.source} onValueChange={(value) => setFormData({ ...formData, source: value })}>
              <SelectTrigger><SelectValue placeholder="Select source" /></SelectTrigger>
              <SelectContent>
                {sources.map((source) => (
                  <SelectItem key={source.id} value={source.name}>{source.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Due Date *</Label>
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button type="button" variant="outline" className="w-full justify-start text-left font-normal">
                  <Calendar className="mr-2 h-4 w-4 text-primary" />
                  {formData.due_date ? formatDisplayDate(formData.due_date) : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={parseISODate(formData.due_date)}
                  onSelect={handleDateSelect}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <p className="text-xs text-muted-foreground">Default: 3 weeks from today</p>
          </div>

          <div className="space-y-2">
            <Label>Starting Status *</Label>
            <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
              <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
              <SelectContent>
                {statuses.map((status) => (
                  <SelectItem key={status.id} value={status.name}>{status.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Add any notes about this order..."
              className="min-h-[80px]"
            />
          </div>

          {invoiceId && (
            <p className="text-xs text-muted-foreground">This order will be linked to invoice {invoiceId.slice(0, 8)}...</p>
          )}

          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="flex-1">
              {saving ? "Creating..." : "Create Order"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
